import React, { ReactNode, useState } from "react";
import {
    Stack,
    Typography,
    Box,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    Grid,
    Avatar,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Button,
    Dialog,
    DialogContent,
    IconButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import HotelIcon from "@mui/icons-material/Hotel";
import PersonIcon from "@mui/icons-material/Person";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import StarIcon from "@mui/icons-material/Star";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import h1 from "../assets/hotel/h1.png";
import h2 from "../assets/hotel/h2.png";
import h3 from "../assets/hotel/h3.png";
import h4 from "../assets/hotel/h4.png";
import h5 from "../assets/hotel/h5.png";
import h6 from "../assets/hotel/h6.png";
import h7 from "../assets/hotel/h7.png";
import h8 from "../assets/hotel/h8.png";
import h9 from "../assets/hotel/h9.png";
import h10 from "../assets/hotel/h10.png";
import h11 from "../assets/hotel/h11.png";
import {useNavigate} from "react-router";
import PATH from "../routing/routes";

// ── Types ──────────────────────────────────────────────────────────────────

interface ScheduleDay {
    date: string;
    title: string;
    description: string;
    highlight?: boolean;
}

interface BoardingPoint {
    city: string;
    detail: string;
}

interface ServiceIcon {
    icon: ReactNode;
    label: string;
}

// ── Constants ──────────────────────────────────────────────────────────────

const hotelImages: string[] = [h1, h2, h3, h4, h5, h6, h7, h8, h9, h10, h11];

const included: string[] = [
    "Transport cu autocar clasificat pentru turism",
    "Ghid însoțitor pe întregul parcurs al taberei",
    "Cazare Hotel ✮✮✮✮ în camere triple și cvadruple – 6 nopți",
    "Regim de masă: pensiune completă",
    "Cursuri de limba greacă veche și latină, predate de profesori experimentați",
    "Material de curs",
    "Excursie de o zi la Templul lui Zeus din Dion și drumeție la poalele Muntelui Olimp",
];

const notIncluded: string[] = [
    "Asigurare de călătorie (se poate încheia printr-un broker colaborator)",
];

const highlights: string[] = [
    "Cursuri zilnice de greacă antică și latină",
    "Relaxare pe plajele însorite ale Greciei",
    "Profesori cu experiență",
    "Activități recreative",
    "Amintiri de neuitat",
    "Excursie de o zi",
    "Concursuri",
];

const schedule: ScheduleDay[] = [
    {
        date: "02.08.2026",
        title: "Plecare din Brașov",
        description:
            "Plecarea se va efectua din parcarea din spatele Bazinului Olimpic Brașov (Sala Sporturilor) la ora 19:00. Pe drum se vor face popasuri frecvente la aproximativ două ore și jumătate – trei ore de mers.",
    },
    {
        date: "03.08.2026",
        title: "Sosire & Acomodare",
        description:
            "Ne propunem să sosim pe riviera grecească în jurul orei 11:00. Prima zi este destinată acomodării, odihnei, cunoașterii împrejurimilor și relaxării la malul Mării Egee. În cursul serii, după cină, va avea loc ședința tehnică a taberei și împărțirea pe grupe de lucru.",
    },
    {
        date: "04.08.2026",
        title: "Prima zi de cursuri",
        description:
            "Dimineața o începem cu servirea micului-dejun, după care vom petrece timp liber la plajă. Grupa 1 – 12:00-14:00 – Curs limba greacă veche, 14:00 – Masa de prânz; Grupa 2 – 13:00 – Masa de prânz, 14:00-16:00 – Curs limba latină. După terminarea cursurilor vom avea timp liber pentru siestă și plajă.",
    },
    {
        date: "05.08.2026",
        title: "Cursuri & Plajă",
        description:
            "Mic-dejun, timp liber la plajă. Grupa 1 – 12:00-14:00 – Curs limba greacă veche, 14:00 – Masa de prânz; Grupa 2 – 13:00 – Masa de prânz, 14:00-16:00 – Curs limba latină, siestă, timp liber la plajă, cină.",
    },
    {
        date: "06.08.2026",
        title: "Excursie: Templul lui Zeus & Muntele Olimp",
        description:
            "Excursie de o zi la Templul lui Zeus din Dion și drumeție la poalele Muntelui Olimp.",
        highlight: true,
    },
    {
        date: "07.08.2026",
        title: "Cursuri & Plajă",
        description:
            "Mic-dejun, timp liber la plajă. Grupa 2 – 12:00-14:00 – Curs limba greacă veche, 14:00 – Masa de prânz; Grupa 1 – 13:00 – Masa de prânz, 14:00-16:00 – Curs limba latină, siestă, timp liber la plajă, cină.",
    },
    {
        date: "08.08.2026",
        title: "Cursuri & Plajă",
        description:
            "Mic-dejun, timp liber la plajă. Grupa 2 – 12:30-14:00 – Curs limba greacă veche, 14:00 – Masa de prânz; Grupa 1 – 13:00 – Masa de prânz, 14:00-15:30 – Curs limba latină, siestă, timp liber la plajă, cină.",
    },
    {
        date: "09.08.2026",
        title: "Întoarcere acasă",
        description:
            "Imediat după servirea micului-dejun, ne vom decaza și ne vom îmbarca în autocar pentru a începe drumul către casă, urmând a ajunge în Brașov la aproximativ ora 23:00. Pe drum vom face un popas pentru servirea mesei de prânz.",
    },
];

const boardingPoints: BoardingPoint[] = [
    { city: "Brașov", detail: "Sala Sporturilor" },
    { city: "Ploiești", detail: "Benzinăria ETU – DN1 – Brașov-București" },
    { city: "București", detail: "Alveola Kiseleff – Zona Muzeului Țăranului Român" },
];

const accommodationFeatures: string[] = [
    "Camere triple și cvadruple",
    "Pensiune completă în regim bufet",
    "Distanță de doar 200 m până la plajă",
];

// ── SectionTitle ───────────────────────────────────────────────────────────

interface SectionTitleProps {
    children: ReactNode;
}

const SectionTitle: React.FC<SectionTitleProps> = ({ children }) => {
    const theme = useTheme();

    return (
        <Typography
            variant="h6"
            fontWeight="bold"
            sx={{
                color: theme.palette.secondary.main,
                textTransform: "uppercase",
                letterSpacing: 1,
                mb: 2,
                mt: 1,
                borderBottom: `3px solid ${theme.palette.primary.main}`,
                pb: 0.5,
                display: "inline-block",
            }}
        >
            {children}
        </Typography>
    );
};

// ── HotelGallery ───────────────────────────────────────────────────────────

const HotelGallery: React.FC = () => {
    const theme = useTheme();
    const [lightboxOpen, setLightboxOpen] = useState<boolean>(false);
    const [activeIndex, setActiveIndex] = useState<number>(0);

    const openLightbox = (index: number): void => {
        setActiveIndex(index);
        setLightboxOpen(true);
    };

    const prev = (): void =>
        setActiveIndex((i) => (i - 1 + hotelImages.length) % hotelImages.length);

    const next = (): void =>
        setActiveIndex((i) => (i + 1) % hotelImages.length);

    const handleKeyDown = (e: React.KeyboardEvent): void => {
        if (e.key === "ArrowLeft") prev();
        if (e.key === "ArrowRight") next();
        if (e.key === "Escape") setLightboxOpen(false);
    };

    return (
        <>
            {/* Thumbnail grid — first image spans 2 rows, rest fill a 2-col side */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr 1fr", md: "2fr 1fr 1fr" },
                    gridTemplateRows: { xs: "120px 120px 120px", md: "160px 160px" },
                    gap: 1,
                    borderRadius: 2,
                    overflow: "hidden",
                }}
            >
                {/* Hero image */}
                <Box
                    component="img"
                    src={hotelImages[0]}
                    alt="Hotel ZOI Girni 1"
                    onClick={() => openLightbox(0)}
                    sx={{
                        gridRow: { xs: "1 / 3", md: "1 / 3" },
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        cursor: "pointer",
                        transition: "opacity 0.2s",
                        "&:hover": { opacity: 0.85 },
                    }}
                />
                {/* Thumbnails */}
                {hotelImages.slice(1).map((src, i) => (
                    <Box
                        key={i}
                        component="img"
                        src={src}
                        alt={`Hotel ZOI Girni ${i + 2}`}
                        onClick={() => openLightbox(i + 1)}
                        sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            cursor: "pointer",
                            transition: "opacity 0.2s",
                            "&:hover": { opacity: 0.85 },
                        }}
                    />
                ))}
            </Box>

            {/* Lightbox */}
            <Dialog
                open={lightboxOpen}
                onClose={() => setLightboxOpen(false)}
                maxWidth="lg"
                fullWidth
                onKeyDown={handleKeyDown}
                PaperProps={{ sx: { bgcolor: "#000", boxShadow: "none" } }}
            >
                <DialogContent
                    sx={{
                        p: 0,
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 400,
                    }}
                >
                    {/* Close */}
                    <IconButton
                        onClick={() => setLightboxOpen(false)}
                        sx={{ position: "absolute", top: 8, right: 8, color: "#fff", zIndex: 10 }}
                    >
                        <CloseIcon />
                    </IconButton>

                    {/* Prev */}
                    <IconButton
                        onClick={prev}
                        sx={{
                            position: "absolute",
                            left: 8,
                            color: "#fff",
                            bgcolor: "rgba(0,0,0,0.4)",
                            "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                            zIndex: 10,
                        }}
                    >
                        <ArrowBackIosNewIcon />
                    </IconButton>

                    <Box
                        component="img"
                        src={hotelImages[activeIndex]}
                        alt={`Hotel ${activeIndex + 1}`}
                        sx={{ maxHeight: "80vh", maxWidth: "100%", objectFit: "contain" }}
                    />

                    {/* Next */}
                    <IconButton
                        onClick={next}
                        sx={{
                            position: "absolute",
                            right: 8,
                            color: "#fff",
                            bgcolor: "rgba(0,0,0,0.4)",
                            "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                            zIndex: 10,
                        }}
                    >
                        <ArrowForwardIosIcon />
                    </IconButton>

                    {/* Dot indicators */}
                    <Stack
                        direction="row"
                        spacing={0.5}
                        sx={{
                            position: "absolute",
                            bottom: 12,
                            left: "50%",
                            transform: "translateX(-50%)",
                        }}
                    >
                        {hotelImages.map((_, i) => (
                            <Box
                                key={i}
                                onClick={() => setActiveIndex(i)}
                                sx={{
                                    width: i === activeIndex ? 20 : 8,
                                    height: 8,
                                    borderRadius: 4,
                                    bgcolor: i === activeIndex
                                        ? theme.palette.primary.main
                                        : "rgba(255,255,255,0.45)",
                                    cursor: "pointer",
                                    transition: "all 0.2s",
                                }}
                            />
                        ))}
                    </Stack>
                </DialogContent>
            </Dialog>
        </>
    );
};

// ── CampsClassics ──────────────────────────────────────────────────────────

const CampsClassics: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();

    const serviceIcons: ServiceIcon[] = [
        { icon: <PersonIcon sx={{ fontSize: 36, color: theme.palette.secondary.main }} />, label: "Ghid Însoțitor" },
        { icon: <HotelIcon sx={{ fontSize: 36, color: theme.palette.secondary.main }} />, label: "Cazare" },
        { icon: <DirectionsBusIcon sx={{ fontSize: 36, color: theme.palette.secondary.main }} />, label: "Transport cu Autocar" },
        { icon: <RestaurantIcon sx={{ fontSize: 36, color: theme.palette.secondary.main }} />, label: "Mic-dejun, Prânz, Cină" },
    ];

    const register = () => {
        window.scrollTo({top: 0, behavior: 'smooth'});
        navigate(`${PATH.REGISTRATIONS}?trip=Limbi_Clasice_3-9_august_2026`);
    }

    return (
        <Stack direction="column" sx={{ bgcolor: "background.default", minHeight: "100vh" }}>

            {/* ── HERO BANNER ── */}
            <Box
                sx={{
                    background: `linear-gradient(135deg, #2d3e80 0%, ${theme.palette.secondary.main} 100%)`,
                    color: "#fff",
                    px: { xs: 2, md: 6 },
                    py: { xs: 4, md: 5 },
                    position: "relative",
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        right: -60,
                        top: -60,
                        width: 280,
                        height: 280,
                        borderRadius: "50%",
                        bgcolor: "primary.main",
                        opacity: 0.2,
                    }}
                />

                <Stack direction="row" spacing={1} flexWrap="wrap" mb={2}>
                    {(["Învățare", "Greacă veche", "Latină", "Relaxare"] as const).map((tag) => (
                        <Chip
                            key={tag}
                            label={tag}
                            size="small"
                            sx={{
                                bgcolor: "rgba(198,163,0,0.2)",
                                color: theme.palette.primary.light,
                                fontWeight: 600,
                                border: `1px solid ${theme.palette.primary.main}`,
                                fontSize: 12,
                            }}
                        />
                    ))}
                </Stack>

                <Typography
                    variant="h3"
                    fontWeight="bold"
                    sx={{ fontFamily: "Georgia, serif", color: "#fff", mb: 1 }}
                >
                    Tabere de Limbi Clasice
                </Typography>

                <Typography
                    variant="h5"
                    sx={{ color: theme.palette.primary.light, fontFamily: "Georgia, serif", fontStyle: "italic", mb: 3 }}
                >
                    Prima ediție – Chiron
                </Typography>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <LocationOnIcon sx={{ color: "primary.main" }} />
                        <Typography variant="body1" fontWeight={500}>Olympiaki Akti, Grecia</Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <CalendarMonthIcon sx={{ color: "primary.main" }} />
                        <Typography variant="body1" fontWeight={500}>3 – 9 August 2026</Typography>
                    </Stack>
                </Stack>

                {/* Price badge */}
                <Box
                    sx={{
                        position: { xs: "relative", md: "absolute" },
                        right: { md: 60 },
                        top: { md: "50%" },
                        transform: { md: "translateY(-50%)" },
                        bgcolor: "primary.main",
                        borderRadius: "50%",
                        width: 140,
                        height: 140,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        mt: { xs: 3, md: 0 },
                        boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
                        zIndex: 1,
                    }}
                >
                    <Typography variant="h4" fontWeight="bold" sx={{ color: "#fff", lineHeight: 1.1 }}>
                        810 €
                    </Typography>
                    <Typography variant="caption" fontWeight={700} sx={{ color: "#fff" }}>
                        DE PERSOANĂ
                    </Typography>
                </Box>
            </Box>

            {/* ── BODY ── */}
            <Box sx={{ px: { xs: 2, md: 6 }, py: 4 }}>

                {/* Highlights */}
                <Grid container spacing={2} mb={4}>
                    {highlights.map((item) => (
                        <Grid key={item} size={{ xs: 12, sm: 6, md: 4 }}>
                            <Stack direction="row" alignItems="center" spacing={1.5}>
                                <CheckCircleIcon sx={{ color: "primary.main", fontSize: 22 }} />
                                <Typography variant="body2" fontWeight={500} sx={{ color: "secondary.main" }}>
                                    {item}
                                </Typography>
                            </Stack>
                        </Grid>
                    ))}
                </Grid>

                {/* Service icons */}
                <Paper elevation={0} sx={{ border: "1px solid #e5e7eb", borderRadius: 3, p: 3, mb: 4, bgcolor: "#fff" }}>
                    <Grid container spacing={2} justifyContent="center" textAlign="center">
                        {serviceIcons.map(({ icon, label }) => (
                            <Grid key={label} size={{ xs: 6, sm: 3 }}>
                                <Stack alignItems="center" spacing={1}>
                                    {icon}
                                    <Typography
                                        variant="caption"
                                        fontWeight={600}
                                        sx={{ color: "secondary.main", textTransform: "uppercase", fontSize: 10, letterSpacing: 0.5 }}
                                    >
                                        {label}
                                    </Typography>
                                </Stack>
                            </Grid>
                        ))}
                    </Grid>
                </Paper>

                {/* Includes / excludes / inscription */}
                <Grid container spacing={3} mb={4}>
                    <Grid size={{ xs: 12, md: 7 }}>
                        <Paper elevation={0} sx={{ border: "1px solid #e5e7eb", borderRadius: 3, p: 3, bgcolor: "#fff", height: "100%" }}>
                            <SectionTitle>Ce include prețul?</SectionTitle>
                            <List dense>
                                {included.map((item) => (
                                    <ListItem key={item} disableGutters sx={{ py: 0.3 }}>
                                        <ListItemIcon sx={{ minWidth: 32 }}>
                                            <CheckIcon sx={{ color: "#16a34a", fontSize: 18 }} />
                                        </ListItemIcon>
                                        <ListItemText primary={item} primaryTypographyProps={{ variant: "body2", color: "#374151" }} />
                                    </ListItem>
                                ))}
                            </List>
                        </Paper>
                    </Grid>

                    <Grid size={{ xs: 12, md: 5 }}>
                        <Stack spacing={3}>
                            <Paper elevation={0} sx={{ border: "1px solid #e5e7eb", borderRadius: 3, p: 3, bgcolor: "#fff" }}>
                                <SectionTitle>Ce NU include prețul?</SectionTitle>
                                <List dense>
                                    {notIncluded.map((item) => (
                                        <ListItem key={item} disableGutters sx={{ py: 0.3 }}>
                                            <ListItemIcon sx={{ minWidth: 32 }}>
                                                <CloseIcon sx={{ color: "#dc2626", fontSize: 18 }} />
                                            </ListItemIcon>
                                            <ListItemText primary={item} primaryTypographyProps={{ variant: "body2", color: "#374151" }} />
                                        </ListItem>
                                    ))}
                                </List>
                            </Paper>

                            <Paper elevation={0} sx={{ border: "1px solid #e5e7eb", borderRadius: 3, p: 3, bgcolor: "#fff" }}>
                                <SectionTitle>Condiții de Înscriere</SectionTitle>
                                <List dense>
                                    {(["Avans de 250 € până la 30.03.2026", "Restul sumei până la 30.06.2026"] as const).map((item) => (
                                        <ListItem key={item} disableGutters sx={{ py: 0.3 }}>
                                            <ListItemIcon sx={{ minWidth: 32 }}>
                                                <CheckCircleIcon sx={{ color: "primary.main", fontSize: 18 }} />
                                            </ListItemIcon>
                                            <ListItemText primary={item} primaryTypographyProps={{ variant: "body2", color: "#374151" }} />
                                        </ListItem>
                                    ))}
                                </List>
                            </Paper>
                        </Stack>
                    </Grid>
                </Grid>

                {/* Schedule */}
                <Box mb={4}>
                    <SectionTitle>Programul Taberei</SectionTitle>
                    <Stack spacing={1}>
                        {schedule.map((day: ScheduleDay, idx: number) => (
                            <Accordion
                                key={day.date}
                                elevation={0}
                                disableGutters
                                sx={{
                                    border: `1px solid ${day.highlight ? theme.palette.primary.main : "#e5e7eb"}`,
                                    borderRadius: "12px !important",
                                    bgcolor: day.highlight ? "#fffbeb" : "#fff",
                                    "&:before": { display: "none" },
                                    overflow: "hidden",
                                }}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2.5, py: 0.5 }}>
                                    <Stack direction="row" alignItems="center" spacing={2} width="100%">
                                        <Avatar
                                            sx={{
                                                bgcolor: day.highlight ? "primary.main" : "secondary.main",
                                                color: "#fff",
                                                width: 36,
                                                height: 36,
                                                fontSize: 13,
                                                fontWeight: 700,
                                            }}
                                        >
                                            {idx + 1}
                                        </Avatar>
                                        <Box>
                                            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
                                                {day.date}
                                            </Typography>
                                            <Typography variant="body2" fontWeight={700} sx={{ color: "secondary.main" }}>
                                                {day.title}
                                            </Typography>
                                        </Box>
                                        {day.highlight && (
                                            <Chip
                                                label="Excursie"
                                                size="small"
                                                icon={<EmojiEventsIcon style={{ fontSize: 14 }} />}
                                                sx={{ ml: "auto", mr: 1, bgcolor: "primary.main", color: "#fff", fontWeight: 700, fontSize: 11 }}
                                            />
                                        )}
                                    </Stack>
                                </AccordionSummary>
                                <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2 }}>
                                    <Typography variant="body2" sx={{ color: "#374151", lineHeight: 1.7, pl: 6.5 }}>
                                        {day.description}
                                    </Typography>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Stack>
                </Box>

                {/* Accommodation + Gallery */}
                <Box mb={4}>
                    <SectionTitle>Condiții de Cazare</SectionTitle>
                    <Paper elevation={0} sx={{ border: "1px solid #e5e7eb", borderRadius: 3, p: 3, bgcolor: "#fff" }}>
                        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                            <HotelIcon sx={{ color: "secondary.main" }} />
                            <Typography variant="subtitle1" fontWeight={700} sx={{ color: "secondary.main" }}>
                                Hotel ZOI Girni – Olympiaki Akti
                            </Typography>
                            <Stack direction="row" ml={1}>
                                {[1, 2, 3, 4].map((s: number) => (
                                    <StarIcon key={s} sx={{ color: "primary.main", fontSize: 16 }} />
                                ))}
                            </Stack>
                        </Stack>
                        <List dense sx={{ mb: 3 }}>
                            {accommodationFeatures.map((item) => (
                                <ListItem key={item} disableGutters sx={{ py: 0.2 }}>
                                    <ListItemIcon sx={{ minWidth: 28 }}>
                                        <CheckIcon sx={{ color: "#16a34a", fontSize: 16 }} />
                                    </ListItemIcon>
                                    <ListItemText primary={item} primaryTypographyProps={{ variant: "body2" }} />
                                </ListItem>
                            ))}
                        </List>
                        <HotelGallery />
                    </Paper>
                </Box>

                {/* Boarding points */}
                <Box mb={4}>
                    <SectionTitle>Puncte de Îmbarcare</SectionTitle>
                    <Paper elevation={0} sx={{ border: "1px solid #e5e7eb", borderRadius: 3, p: 3, bgcolor: "#fff" }}>
                        <Grid container spacing={2}>
                            {boardingPoints.map(({ city, detail }: BoardingPoint) => (
                                <Grid key={city} size={{ xs: 12, sm: 4 }}>
                                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                        <LocationOnIcon sx={{ color: "primary.main", mt: 0.3, flexShrink: 0 }} />
                                        <Box>
                                            <Typography variant="body2" fontWeight={700} sx={{ color: "secondary.main" }}>
                                                {city}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                                {detail}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Grid>
                            ))}
                        </Grid>
                        <Typography variant="caption" sx={{ color: "text.secondary", mt: 2, display: "block" }}>
                            La cerere se pot efectua îmbarcări și din: Predeal, Azuga, Bușteni, Sinaia, Comarnic și Câmpina.
                        </Typography>
                    </Paper>
                </Box>

                {/* Registrations */}
                <Box display="flex" justifyContent="center">
                    <Button
                        onClick={register}
                        variant="contained"
                        sx={{ color: "white", fontWeight: 700, fontSize: "20px" }}
                    >
                        Înscriere
                    </Button>
                </Box>

            </Box>
        </Stack>
    );
};

export default CampsClassics;